import React, { useEffect, useMemo, useRef, useState } from "react";
import { Handle, Position, useEdges, useNodes, useViewport } from "@xyflow/react";
import { Bot, Check, Loader2, RefreshCw, Send, Sparkles } from "lucide-react";
import { useToast } from "../../../context/ToastContext";
import { createAssistantConversation } from "../../../services/assistantService";
import { runTaskToCompletion } from "../../../services/taskService";
import {
  DIRECTOR_OPTIMIZE_REQUEST,
  REGENERATABLE_LEGACY_TYPES,
  buildDirectorMessage,
  collectDirectorPeers,
  parseDirectorSuggestions,
} from "../utils/directorContext";

// 片5 AI 导演对话节点（type:'atom' + data.legacyType:'aiDirector'）
// 钉在画布上的对话气泡卡片：自动携带连入节点（reference 边，不限类型）的
// 名称/类型/提示词/内容/状态作为上下文，与后端 assistant 域多轮对话；
// 支持两个快捷动作（优化相连节点提示词 / 重新生成选中节点），
// 导演回复中的改写建议可逐条"应用到节点"（走既有 onUpdate → 保存队列 + 撤销）。
//
// 连线：仅左入（上下文输入），不渲染输出 Handle，connectionRules 同时禁出边。
// 持久化：messages（限 20 条滚动覆盖）+ conversationId 存 data，随保存队列/快照。

const MAX_MESSAGES = 20;
const ACCENT = "var(--color-accent-primary)";

const AiDirectorNode = ({ data, id, selected }) => {
  const toast = useToast();
  const edges = useEdges();
  const nodes = useNodes();
  const [hovered, setHovered] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  // 片5 语义缩放：简卡（zoom < 0.45）只留标题行（CSS 隐藏卡片主体，不卸载 DOM）
  const { zoom } = useViewport();
  const zoomTier = zoom < 0.45 ? "simple" : zoom > 0.8 ? "full" : "compact";

  // 连入上下文节点（连线变化即时反映到徽章与空态文案）
  const peers = useMemo(() => collectDirectorPeers(id, edges, nodes), [id, edges, nodes]);
  const messages = Array.isArray(data.messages) ? data.messages : [];
  // 可优化提示词的相连节点（有 prompt/imagePrompt/videoPrompt）
  const promptPeerCount = useMemo(
    () =>
      peers.filter((n) => {
        const d = n.data || {};
        return (d.prompt || d.imagePrompt || d.videoPrompt || "").trim();
      }).length,
    [peers]
  );

  // 新消息/收发状态变化时滚到底部
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, busy]);

  const persist = (patch) => data.onUpdate?.(id, patch);

  // 发送主流程（用户消息与预制动作共用）
  // userText: 发给导演的原始请求；displayText: 用户气泡里显示的短文案
  // restoreOnFail: 失败时把内容放回输入框（仅手动发送）
  const sendToDirector = async (userText, displayText, { restoreOnFail = false } = {}) => {
    if (busy) return;
    setBusy(true);
    const withUser = [...messages, { role: "user", content: displayText, ts: Date.now() }].slice(-MAX_MESSAGES);
    persist({ messages: withUser });
    // 在 try 外声明：catch 分支也需要把已建出的会话 id 落盘（避免孤儿会话）
    let createdConversationId = null;
    try {
      const projectId = data.projectId || localStorage.getItem("currentProjectId") || null;
      let conversationId = data.conversationId || null;
      if (!conversationId) {
        // 首次发送：为节点建独立 assistant 会话（多轮历史挂载在它上面）
        const conv = await createAssistantConversation({ projectId, title: data.name || "画布 AI 导演" });
        conversationId = conv?.id ?? null;
        if (!conversationId) throw new Error("导演对话初始化失败");
        createdConversationId = conversationId;
      }
      const task = await runTaskToCompletion(
        "AGENT_CHAT",
        {
          projectId: projectId ? Number(projectId) : null,
          message: buildDirectorMessage(userText, peers),
          // 不注入项目剧本上下文，避免稀释画布节点上下文（上下文由前端装配进 message）
          scriptEnabled: false,
          scriptScope: "all",
          scriptEpisodeIds: [],
          conversationId,
        },
        {
          provider: localStorage.getItem("ai_provider") || "omini",
          projectId: projectId ? Number(projectId) : null,
          source: "canvas_ai_director",
          timeoutMs: 10 * 60 * 1000,
        }
      );
      const rawReply =
        typeof task.result === "string"
          ? task.result
          : task.result?.value || task.result?.result || task.result?.reply || "";
      const { text, suggestions } = parseDirectorSuggestions(rawReply || "（无返回内容）");
      // 正文被 suggestions 代码块剥离后可能为空：有建议时给一句过渡语
      const replyText = text || (suggestions.length > 0 ? "改写建议如下，逐条点「应用到节点」即可落回画布：" : "（无返回内容）");
      const finalMessages = [
        ...withUser,
        { role: "assistant", content: replyText, suggestions, ts: Date.now() },
      ].slice(-MAX_MESSAGES);
      persist({
        messages: finalMessages,
        ...(createdConversationId ? { conversationId: createdConversationId } : {}),
      });
    } catch (err) {
      console.error("[AiDirector] 发送失败:", err);
      toast.error("导演回复失败: " + (err?.message || "请重试"));
      persist({
        messages: [
          ...withUser,
          { role: "assistant", content: `出错了: ${err?.message || "请重试"}`, ts: Date.now() },
        ].slice(-MAX_MESSAGES),
        // 会话已建出但请求失败：conversationId 照样落盘，避免下次再建产生孤儿会话
        ...(createdConversationId ? { conversationId: createdConversationId } : {}),
      });
      if (restoreOnFail) setInput(userText);
    } finally {
      setBusy(false);
    }
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    sendToDirector(text, text, { restoreOnFail: true });
  };

  // 快捷动作 1：优化相连节点提示词（改写建议随回复以 suggestions 返回，逐条应用）
  const handleOptimize = () => {
    if (busy) return;
    if (promptPeerCount === 0) {
      toast.warning("先把带提示词的分镜/生成节点连给我");
      return;
    }
    sendToDirector(DIRECTOR_OPTIMIZE_REQUEST, `优化这 ${promptPeerCount} 个相连节点的提示词`);
  };

  // 快捷动作 2：重新生成选中节点（复用既有 handleNodeGenerate，与 AtomNode 重生成按钮同通道）
  const handleRegenerateSelected = () => {
    if (busy) return;
    const targets = nodes.filter(
      (n) =>
        n.selected &&
        REGENERATABLE_LEGACY_TYPES.has(n.data?.legacyType || n.type) &&
        !n.data?.isGenerating
    );
    if (targets.length === 0) {
      toast.warning("先选中可生成的节点（生成/分镜类）");
      return;
    }
    targets.forEach((n) => data.onGenerate?.(n.id, n.data));
    toast.info(`已触发 ${targets.length} 个节点重新生成`);
  };

  // 改写建议落回节点 data（storyboardShot/Grid 同步 imagePrompt，与 AtomEditPanel 口径一致）
  const applySuggestion = (msgIndex, suggestion, suggestionIndex) => {
    const target = nodes.find((n) => n.id === String(suggestion.nodeId));
    if (!target) {
      toast.warning("目标节点已不存在");
      return;
    }
    const legacy = target.data?.legacyType || target.type;
    const patch = { prompt: suggestion.prompt };
    if (legacy === "storyboardShot" || legacy === "storyboardGrid") {
      patch.imagePrompt = suggestion.prompt;
    }
    data.onUpdate?.(target.id, patch);
    persist({
      messages: messages.map((m, i) =>
        i === msgIndex
          ? {
              ...m,
              suggestions: (m.suggestions || []).map((s, j) =>
                j === suggestionIndex ? { ...s, applied: true } : s
              ),
            }
          : m
      ),
    });
    const targetName =
      target.data?.name || (target.data?.shotNumber != null ? `分镜#${target.data.shotNumber}` : "节点");
    toast.success(`已应用到「${targetName}」`);
  };

  // 建议卡片里的目标节点名（渲染期解析，节点被删后给禁用态）
  const resolveSuggestionName = (suggestion) => {
    const target = nodes.find((n) => n.id === String(suggestion.nodeId));
    if (!target) return null;
    return (
      target.data?.name ||
      (target.data?.shotNumber != null ? `分镜#${target.data.shotNumber}` : "节点")
    );
  };

  return (
    <div
      className={`relative atom-node atom-zoom-${zoomTier}`}
      style={{ width: 300 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* 连接点：仅左入（上下文输入）；出边由 connectionRules 禁止 */}
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        style={{
          width: 20,
          height: 20,
          background: hovered ? "var(--color-bg-tertiary)" : "transparent",
          border: hovered ? "2px solid var(--color-border-primary)" : "2px solid transparent",
          borderRadius: "50%",
          cursor: "crosshair",
          transition: "all 0.2s ease",
        }}
      />

      {/* ── 卡片外：标题行（图标 + 名称 + 连入计数徽章） ── */}
      <div className="flex items-center justify-between px-0.5 mb-1.5">
        <div className="flex items-center gap-1.5 min-w-0">
          <Bot size={12} style={{ color: ACCENT }} />
          <span className="text-xs truncate" style={{ color: "var(--color-text-secondary)", maxWidth: 180 }}>
            {data.name || "AI 导演"}
          </span>
        </div>
        <span
          className="text-2xs font-mono-num px-1.5 py-0.5 rounded"
          style={{
            background: `color-mix(in srgb, ${ACCENT} 14%, transparent)`,
            color: ACCENT,
            border: `1px solid color-mix(in srgb, ${ACCENT} 32%, transparent)`,
          }}
          title="连入的上下文节点数"
        >
          {peers.length} 上下文
        </span>
      </div>

      {/* ── 卡片：消息区 + 快捷动作 + 输入区（atom-detail：简卡模式 CSS 隐藏） ── */}
      <div
        className="atom-detail relative flex flex-col"
        style={{
          borderRadius: 10,
          overflow: "hidden",
          border: selected
            ? `2px solid ${ACCENT}`
            : hovered
              ? "1px solid var(--color-border-primary)"
              : "1px solid var(--color-border-secondary)",
          boxShadow: selected
            ? "0 0 0 3px var(--color-accent-light), inset 0 1px 0 rgba(255,255,255,0.05), 0 12px 24px -8px rgba(0,0,0,0.5), 0 32px 64px -24px rgba(0,0,0,0.35)"
            : "inset 0 1px 0 rgba(255,255,255,0.05), 0 12px 24px -8px rgba(0,0,0,0.5), 0 32px 64px -24px rgba(0,0,0,0.35)",
          transition: "border 0.2s ease, box-shadow 0.3s ease",
          background: "var(--color-bg-secondary)",
        }}
      >
        {/* 消息滚动区 */}
        <div
          ref={scrollRef}
          className="nodrag nowheel custom-scrollbar flex flex-col gap-2 px-2.5 py-2.5"
          style={{ maxHeight: 260, minHeight: 96, overflowY: "auto" }}
        >
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 py-5 text-center">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{
                  background: `color-mix(in srgb, ${ACCENT} 12%, transparent)`,
                  border: `1px solid color-mix(in srgb, ${ACCENT} 28%, transparent)`,
                }}
              >
                <Bot size={20} style={{ color: ACCENT }} />
              </div>
              {peers.length === 0 ? (
                <>
                  <span className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
                    连一些节点给我，我就能看着它们聊
                  </span>
                  <span className="text-2xs leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                    把分镜 / 图片 / 剧本节点的输出连到我左侧
                  </span>
                </>
              ) : (
                <>
                  <span className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
                    我在看着 {peers.length} 个节点，问我点什么吧
                  </span>
                  <span className="text-2xs leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                    比如：这几个分镜节奏怎么样？帮我改改提示词
                  </span>
                </>
              )}
            </div>
          )}

          {messages.map((msg, msgIndex) =>
            msg.role === "user" ? (
              <div key={msgIndex} className="flex justify-end animate-in fade-in slide-in-from-bottom-2 duration-200">
                <div
                  className="px-2.5 py-1.5 rounded-lg text-xs leading-relaxed"
                  style={{
                    maxWidth: "85%",
                    background: `color-mix(in srgb, ${ACCENT} 16%, transparent)`,
                    border: `1px solid color-mix(in srgb, ${ACCENT} 30%, transparent)`,
                    color: "var(--color-text-primary)",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {msg.content}
                </div>
              </div>
            ) : (
              <div key={msgIndex} className="flex flex-col gap-1.5 animate-in fade-in slide-in-from-bottom-2 duration-200">
                <div className="flex justify-start">
                  <div
                    className="px-2.5 py-1.5 rounded-lg text-xs leading-relaxed"
                    style={{
                      maxWidth: "92%",
                      background: "var(--color-bg-tertiary)",
                      border: "1px solid var(--color-border-secondary)",
                      color: "var(--color-text-secondary)",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {msg.content}
                  </div>
                </div>
                {/* 改写建议卡片：逐条"应用到节点" */}
                {(msg.suggestions || []).map((suggestion, sIdx) => {
                  const targetName = resolveSuggestionName(suggestion);
                  const disabled = !!suggestion.applied || !targetName;
                  return (
                    <div
                      key={sIdx}
                      className="rounded-md px-2 py-1.5 flex flex-col gap-1"
                      style={{
                        marginLeft: 10,
                        background: `color-mix(in srgb, ${ACCENT} 8%, transparent)`,
                        border: `1px solid color-mix(in srgb, ${ACCENT} 24%, transparent)`,
                      }}
                    >
                      <div className="flex items-center gap-1 min-w-0">
                        <Sparkles size={10} style={{ color: ACCENT, flexShrink: 0 }} />
                        <span className="text-2xs truncate" style={{ color: ACCENT }}>
                          {targetName || `节点 ${suggestion.nodeId}（已删除）`}
                        </span>
                        {suggestion.reason && (
                          <span className="text-2xs truncate" style={{ color: "var(--color-text-muted)" }} title={suggestion.reason}>
                            · {suggestion.reason}
                          </span>
                        )}
                      </div>
                      <p
                        className="text-2xs leading-relaxed line-clamp-3"
                        style={{ color: "var(--color-text-secondary)", wordBreak: "break-word" }}
                        title={suggestion.prompt}
                      >
                        {suggestion.prompt}
                      </p>
                      <div className="flex justify-end">
                        <button
                          className="nodrag px-2 rounded text-2xs font-medium flex items-center gap-1"
                          style={{
                            height: 22,
                            background: suggestion.applied
                              ? "var(--color-bg-tertiary)"
                              : `color-mix(in srgb, ${ACCENT} 18%, transparent)`,
                            color: suggestion.applied ? "var(--color-text-muted)" : ACCENT,
                            border: `1px solid color-mix(in srgb, ${ACCENT} 30%, transparent)`,
                            cursor: disabled ? "default" : "pointer",
                            opacity: disabled && !suggestion.applied ? 0.5 : 1,
                          }}
                          disabled={disabled}
                          onClick={() => applySuggestion(msgIndex, suggestion, sIdx)}
                        >
                          {suggestion.applied ? (
                            <>
                              <Check size={10} />
                              已应用
                            </>
                          ) : (
                            "应用到节点"
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}

          {busy && (
            <div className="flex items-center gap-1.5 px-1 py-0.5">
              <Loader2 size={12} className="animate-spin" style={{ color: ACCENT }} />
              <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>
                导演思考中...
              </span>
            </div>
          )}
        </div>

        {/* 快捷动作行 */}
        <div
          className="flex items-center gap-1.5 px-2.5 py-1.5"
          style={{ borderTop: "1px solid var(--color-border-secondary)" }}
        >
          <span className="text-label shrink-0">动作</span>
          <button
            className="nodrag px-2 rounded text-2xs font-medium flex items-center gap-1 transition-colors"
            style={{
              height: 22,
              background: "var(--color-bg-tertiary)",
              color: promptPeerCount > 0 ? "var(--color-text-secondary)" : "var(--color-text-muted)",
              border: "1px solid var(--color-border-secondary)",
              cursor: busy ? "default" : "pointer",
              opacity: busy ? 0.5 : 1,
            }}
            title={promptPeerCount > 0 ? `优化 ${promptPeerCount} 个相连节点的提示词` : "先把带提示词的节点连给我"}
            onClick={handleOptimize}
            disabled={busy}
          >
            <Sparkles size={10} style={{ color: ACCENT }} />
            优化提示词
          </button>
          <button
            className="nodrag px-2 rounded text-2xs font-medium flex items-center gap-1 transition-colors"
            style={{
              height: 22,
              background: "var(--color-bg-tertiary)",
              color: "var(--color-text-secondary)",
              border: "1px solid var(--color-border-secondary)",
              cursor: busy ? "default" : "pointer",
              opacity: busy ? 0.5 : 1,
            }}
            title="对当前选中且可生成的节点触发重新生成"
            onClick={handleRegenerateSelected}
            disabled={busy}
          >
            <RefreshCw size={10} style={{ color: ACCENT }} />
            重新生成选中
          </button>
        </div>

        {/* 输入区（点击不冒泡到节点选择/展开逻辑） */}
        <div
          className="flex items-end gap-1.5 px-2.5 py-2"
          style={{ borderTop: "1px solid var(--color-border-secondary)" }}
          onClick={(e) => e.stopPropagation()}
        >
          <textarea
            className="nodrag nowheel flex-1 bg-transparent outline-none text-xs resize-none"
            style={{
              color: "var(--color-text-primary)",
              lineHeight: 1.5,
              maxHeight: 72,
            }}
            rows={1}
            value={input}
            placeholder={peers.length > 0 ? "看着这些节点聊聊..." : "先连线，或直接聊聊..."}
            disabled={busy}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <button
            className="nodrag w-7 h-7 rounded-md flex items-center justify-center shrink-0 transition-all"
            style={{
              background: input.trim() && !busy ? ACCENT : "var(--color-bg-tertiary)",
              color: input.trim() && !busy ? "var(--color-accent-contrast)" : "var(--color-text-muted)",
              cursor: input.trim() && !busy ? "pointer" : "default",
            }}
            title="发送"
            onClick={handleSend}
            disabled={!input.trim() || busy}
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(AiDirectorNode);
